/* eslint-disable complexity */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import Button from 'carbon-react/lib/components/button';

import { LiveReportList } from '@futrli-sdk/predict';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardFinished } from '@futrli/ui-components/components/sidebar/wizard/wizard-finished/wizard-finished';
import { WizardLoading } from '@futrli/ui-components/components/sidebar/wizard/wizard-loading/wizard-loading';
import { FooterMode, WizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ReportDocumentView } from '@futrli/views/views/reports/ReportDocumentView/ReportDocumentView';

import { getExportedReportDetails } from '../../../core/api/reporting/report-document-export';
import { useRouteIds } from '../../../hooks/useRouteIds';
import { downloadPdfFile, openPdfForViewing } from '../../utils/pdf-actions';
import { useExportPolling } from '../export-report-sidebar/hooks/use-export-polling';

export interface ViewPdfWizardContainerProps {
  isOpen: boolean;
  onClose: () => void;
  report: LiveReportList;
}

type Screen = 'generating' | 'ready' | 'error';

/**
 * Generates the latest version of a report as a PDF, showing a spinner while it renders.
 * Once ready, the PDF is downloaded automatically, and "View PDF" and "Download again" buttons
 * in the footer become enabled; if generation fails, an error is shown in place of the spinner
 * and a "Try again" button replaces the footer actions.
 *
 * A fresh PDF is generated every time the sidebar is opened.
 */
export const ViewPdfWizardContainer = ({ isOpen, onClose, report }: ViewPdfWizardContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(() => new ReportDocumentView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const [screen, setScreen] = useState<Screen>('generating');
  const [error, setError] = useState<string | undefined>();
  const [downloadLink, setDownloadLink] = useState<string | undefined>();
  const [hasAutoDownloaded, setHasAutoDownloaded] = useState(false);

  const { isCompleted, errorMessage: pollingError, reset: resetPolling } = useExportPolling({
    exportedReportId: view.state.exportedReportId,
    workspaceId,
    organisationId,
    isConsolidation,
    enabled: isOpen && screen === 'generating' && !view.state.isExporting
  });

  const startExport = useCallback(() => {
    setScreen('generating');
    setError(undefined);
    setDownloadLink(undefined);
    setHasAutoDownloaded(false);
    resetPolling();
    view.exportReportToPdf({ data: { report_id: report.id, name: report.name } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.id, report.name, resetPolling]);

  useEffect(() => {
    if (isOpen) {
      startExport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (view.state.errorExporting) {
      setError(intl.formatMessage({ id: 'reporting.try_again', defaultMessage: 'Try again.' }));
      setScreen('error');
    }
  }, [view.state.errorExporting, intl]);

  useEffect(() => {
    if (pollingError) {
      setError(pollingError);
      setScreen('error');
    }
  }, [pollingError]);

  useEffect(() => {
    const exportedReportId = view.state.exportedReportId;

    if (!isCompleted || !exportedReportId || screen !== 'generating') {
      return undefined;
    }

    let cancelled = false;

    void (async () => {
      try {
        const details = await getExportedReportDetails(workspaceId, organisationId, exportedReportId, isConsolidation);

        if (cancelled) {
          return;
        }

        setDownloadLink(details.download_link);
        setScreen('ready');
      } catch {
        if (!cancelled) {
          setError(intl.formatMessage({ id: 'reporting.try_again', defaultMessage: 'Try again.' }));
          setScreen('error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isCompleted, view.state.exportedReportId, workspaceId, organisationId, isConsolidation, intl, screen]);

  const handleViewPdf = useCallback(() => {
    if (downloadLink) {
      void openPdfForViewing(downloadLink);
    }
  }, [downloadLink]);

  const handleDownloadPdf = useCallback(() => {
    if (downloadLink) {
      void downloadPdfFile(downloadLink, `${report.name}.pdf`);
      setHasAutoDownloaded(true);
    }
  }, [downloadLink, report.name]);

  useEffect(() => {
    if (screen === 'ready' && downloadLink && !hasAutoDownloaded) {
      handleDownloadPdf();
    }
  }, [screen, downloadLink, hasAutoDownloaded, handleDownloadPdf]);

  const isReady = screen === 'ready';

  const downloadButtonText = hasAutoDownloaded
    ? intl.formatMessage({ id: 'predict.actions.download_pdf_again', defaultMessage: 'Download again' })
    : intl.formatMessage({ id: 'predict.actions.download_pdf', defaultMessage: 'Download PDF' });

  const viewButton = (
    <Button key="view" buttonType="tertiary" onClick={handleViewPdf} disabled={!isReady}>
      {intl.formatMessage({ id: 'predict.actions.view_pdf', defaultMessage: 'View PDF' })}
    </Button>
  );

  const downloadButton = (
    <Button key="download" buttonType="primary" onClick={handleDownloadPdf} disabled={!isReady}>
      {downloadButtonText}
    </Button>
  );

  const footerButtons =
    screen === 'error' ? (
      <Button buttonType="primary" onClick={startExport}>
        {intl.formatMessage({ id: 'common.tryAgain', defaultMessage: 'Try again' })}
      </Button>
    ) : (
      <>
        {viewButton}
        {downloadButton}
      </>
    );

  return (
    <WizardScreen footerMode={FooterMode.NONE} onCancel={onClose} rightSideButtons={footerButtons}>
      {screen === 'error' && <WizardFormError message={error} />}
      {screen === 'generating' && (
        <WizardLoading text={intl.formatMessage({ id: 'reporting.generating_pdf', defaultMessage: 'Generating PDF...' })} />
      )}
      {screen === 'ready' && (
        <WizardFinished text={intl.formatMessage({ id: 'reporting.pdf_ready', defaultMessage: 'Your PDF is ready' })} />
      )}
    </WizardScreen>
  );
};
